/**
 * CredentialRevokedNotice (TV1-2446 slice 6, design D4)
 *
 * The inline line rendered next to an action that is disabled because the channel's credential
 * was revoked: why it is disabled, plus a Reconnect call to action. The CTA links to the channel
 * detail page, which hosts the Reconnect action. ON that page (`onReconnect` given, slice 5) the CTA
 * triggers the page's Reconnect action instead, so the page has one Reconnect, not a self-link.
 *
 * Copy arrives already translated, so any domain (channels, batch processes) can render it in
 * its own namespace.
 */

import { Anchor, Group, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Link } from 'react-router';
import { integrationsTestIds } from '@domains/integrations/testids';
import { channelReconnectPath } from '../../state/credentialRevocation';
import classes from './CredentialRevokedNotice.module.css';

export type CredentialRevokedNoticeProps = {
  integrationId: string;
  /** Already translated: why the action is disabled. */
  message: string;
  /** Already translated: the Reconnect call-to-action label. */
  reconnectLabel: string;
  /** The container's testid, from the rendering domain's registry. */
  testId: string;
  /** The Reconnect action of the page rendering the notice; given only on the channel detail page. */
  onReconnect?: () => void;
};

export const CredentialRevokedNotice = ({ integrationId, message, reconnectLabel, testId, onReconnect }: CredentialRevokedNoticeProps ) => (
  <Group gap={6} wrap="nowrap" align="center" className={classes.notice} data-testid={testId}>
    <IconAlertTriangle size={14} className={classes.icon} />
    <Text className={classes.message}>
      {message}{' '}
      {onReconnect ? (
        <Anchor
          component="button"
          type="button"
          onClick={onReconnect}
          className={`${classes.reconnect} ${classes.reconnectButton}`}
          data-testid={integrationsTestIds.channelCredentialRevokedReconnect}
        >
          {reconnectLabel}
        </Anchor>
      ) : (
        <Anchor
          component={Link}
          to={channelReconnectPath( integrationId )}
          className={classes.reconnect}
          data-testid={integrationsTestIds.channelCredentialRevokedReconnect}
        >
          {reconnectLabel}
        </Anchor>
      )}
    </Text>
  </Group>
);

export default CredentialRevokedNotice;
